import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"
import Dropzone from "@deltablot/dropzone"

// Disable Dropzone's automatic class-based discovery; we wire it up via
// Stimulus instead. Set once at module load, not on every connect().
Dropzone.autoDiscover = false

const PREVIEW_TEMPLATE = `
<div class="dz-preview dz-file-preview">
  <div class="dz-image"><img data-dz-thumbnail /></div>
  <div class="dz-details">
    <span class="dz-filename" data-dz-name></span>
    <div class="dz-meta">
      <span class="dz-size" data-dz-size></span>
      <div class="dz-progress"><span class="dz-upload" data-dz-uploadprogress></span></div>
    </div>
  </div>
  <div class="dz-error-message"><span data-dz-errormessage></span></div>
  <button type="button" class="dz-remove" data-dz-remove aria-label="Remove file">&times;</button>
</div>
`

// Connects to data-controller="dropzone"
// Adapted from https://github.com/lazaronixon/css-zero with three changes:
//  1. Imports come from local node_modules, not esm.sh.
//  2. Submission is manual via a Stimulus-target submit button (label and
//     disabled state driven by Dropzone's lifecycle events).
//  3. Apothecary-styled previewTemplate.
export default class extends Controller {
  static targets = ["submit"]
  static values = {
    url: String,
    paramName: String,
    maxFiles: { type: Number, default: 0 },
    maxFilesize: { type: Number, default: 10 },
    acceptedFiles: String
  }

  connect() {
    this.dropZone = new Dropzone(this.element, {
      url: this.urlValue,
      paramName: this.paramNameValue,
      maxFiles: this.maxFilesValue || null,
      maxFilesize: this.maxFilesizeValue,
      acceptedFiles: this.acceptedFilesValue,
      autoProcessQueue: true,
      previewTemplate: PREVIEW_TEMPLATE,
      previewsContainer: this.element.querySelector(".dz-previews"),
      addRemoveLinks: false
    })

    this.dropZone.enqueueFile = file => this.#upload(file)

    const refresh = () => this.#updateSubmitButton()
    this.dropZone.on("addedfile", refresh)
    this.dropZone.on("removedfile", refresh)
    this.dropZone.on("complete", refresh)

    this.element.dataset.dropzoneConnected = "true"
  }

  disconnect() {
    if (this.dropZone) this.dropZone.destroy()
  }

  handleSubmitEnd(event) {
    if (event.detail?.success) {
      this.dropZone.removeAllFiles(true)
    }
  }

  #upload(file) {
    new Uploader(file, this.dropZone).start()
  }

  #updateSubmitButton() {
    if (!this.hasSubmitTarget) return

    const button = this.submitTarget
    const total = this.dropZone.files.length
    const inFlight = this.dropZone.getUploadingFiles().length + this.dropZone.getQueuedFiles().length
    const succeeded = this.dropZone.getFilesWithStatus(Dropzone.SUCCESS).length

    if (total === 0) {
      button.hidden = true
      button.disabled = true
      button.textContent = "Upload"
      return
    }

    button.hidden = false

    if (inFlight > 0) {
      button.disabled = true
      button.textContent = "Uploading…"
    } else if (succeeded === 0) {
      button.disabled = true
      button.textContent = "No files to upload"
    } else {
      button.disabled = false
      button.textContent = succeeded === 1 ? "Upload 1 file" : `Upload ${succeeded} files`
    }
  }
}

class Uploader {
  constructor(file, dropZone) {
    this.file = file
    this.dropZone = dropZone
  }

  start() {
    this.#createDirectUpload((error, blob) => {
      if (error) return this.#emitError(error)
      this.#appendHiddenInput(blob.signed_id)
      this.#emitSuccess()
    })
  }

  // Called by ActiveStorage's DirectUpload before the XHR begins.
  directUploadWillStoreFileWithXHR(xhr) {
    this.file.xhr = xhr
    this.#bindProgress()
    this.#emitProcessing()
  }

  #createDirectUpload(callback) {
    new DirectUpload(this.file, this.dropZone.options.url, this).create(callback)
  }

  #appendHiddenInput(signedId) {
    const input = document.createElement("input")
    input.type = "hidden"
    input.name = this.dropZone.options.paramName
    input.value = signedId

    this.file.previewElement.appendChild(input)
  }

  #bindProgress() {
    this.file.xhr.upload.addEventListener("progress", e => this.#progress(e))
  }

  #progress(event) {
    const progress = (100 * event.loaded) / event.total
    this.dropZone.emit("uploadprogress", this.file, progress, event.loaded)
  }

  #emitProcessing() {
    this.file.status = Dropzone.PROCESSING
    this.dropZone.emit("processing", this.file)
  }

  #emitSuccess() {
    this.file.status = Dropzone.SUCCESS
    this.dropZone.emit("success", this.file)
    this.dropZone.emit("complete", this.file)
  }

  #emitError(error) {
    this.file.status = Dropzone.ERROR
    this.dropZone.emit("error", this.file, error)
    this.dropZone.emit("complete", this.file)
  }
}
